/** Position → clock label for a day, from the admin's Day plan (shared/dayplan.ts positionTimes). */
import { useCallback, useMemo } from 'react';
import { formatClock, gapMinutes, lanePosition, planDay, positionTimes, type LaneSpots, type PlannedPosition, type PlannedVisit, type PositionTime } from '@shared/dayplan';
import { serviceMinutesNow } from '@shared/dates';
import { periodAt } from '@shared/visits';
import type { TodayVisit } from '@shared/api-types';
import { useDayPlan } from './queries';

/**
 * The day laid out (1.5.0): each visit's span from its own length, and — today, once the crew has started — the
 * expected time of each later visit (shared/dayplan.ts planDay). Times stay labels: nothing here is stored.
 */
export function plannedFromToday(visits: readonly TodayVisit[]): PlannedVisit[] {
  const out: PlannedVisit[] = [];
  for (const v of visits) {
    if (v.position === null || v.kind === 'facility' || v.status === 'Cancelled' || v.status === 'Rescheduled' || v.status === 'Proposed') continue;
    // 1.5.16: entered with permission is an arrival too
    const arrivedAt = v.arrival && (v.arrival.answer === 'home' || v.arrival.answer === 'permission') ? serviceMinutesNow(new Date(v.arrival.at)) : null;
    // 1.7.8: End on a staff unit or an office is its finish (the time goes back to the day)
    const ended = v.closeout ?? (v.marked && v.status === 'Completed' ? v.marked : null);
    const finishedAt = ended && (v.status === 'Completed' || v.status === 'In Progress') ? serviceMinutesNow(new Date(ended.at)) : null;
    // 1.7.8: a start the office chose today pins the visit (shared/dayplan.ts planDay)
    const chosen = v.chosen ? { startOverride: v.chosen.start, ...(v.chosen.booked ? { overrideFree: true } : {}) } : {};
    out.push({ position: v.position, minutes: v.lengthMinutes ?? 0, arrivedAt, finishedAt, ...chosen });
  }
  return out;
}

/** 1.6.6: `lanes` on a two-crew day — each lane is laid out on its own clock (null: one crew, as always). */
export function useDayLayout(day: string, visits: readonly TodayVisit[], count: number, live: boolean, lanes: LaneSpots | null = null): PlannedPosition[] {
  const plan = useDayPlan();
  const settings = plan.data?.settings;
  return useMemo(
    () => planDay(day, settings ?? {}, plannedFromToday(visits), count, { ...(live ? { live: true, nowMinutes: serviceMinutesNow(), gapMinutes: gapMinutes(settings ?? {}) } : {}), ...(lanes ? { lanes } : {}) }),
    [day, settings, visits, count, live, lanes],
  );
}

export function usePositionTimes() {
  const plan = useDayPlan();
  const settings = plan.data?.settings;
  // 1.6.6: on a two-crew day a position's label is the time of its place in its own lane
  const lanes = plan.data?.lanes;
  const times = useCallback((dayKey: string, atLeast = 0): PositionTime[] => positionTimes(dayKey, settings ?? {}, atLeast), [settings]);
  const label = useCallback(
    (dayKey: string, position: number | null): string | null => {
      if (!position) return null;
      const at = lanePosition(lanes?.[dayKey], position);
      const p = positionTimes(dayKey, settings ?? {}, at).find((x) => x.position === at);
      return p ? formatClock(p.startMinutes) : null;
    },
    [settings, lanes],
  );
  /**
   * 1.6.1: the period a line shows — from the time the day plan gives its position (shared/visits.ts periodAt), not
   * from the imported ServicePeriod column. A line with no time (a proposal in pencil) keeps what it was given.
   */
  const period = useCallback(
    (dayKey: string, position: number | null, given: string): string => {
      if (!position) return given;
      const at = lanePosition(lanes?.[dayKey], position);
      return periodAt(positionTimes(dayKey, settings ?? {}, at).find((x) => x.position === at)?.startMinutes) ?? given;
    },
    [settings, lanes],
  );
  return { times, label, period, ready: !!plan.data || plan.isError };
}
